import React, { useEffect, useMemo } from 'react';
import { sanitize } from 'dompurify';
import { useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';
import { usePathParam } from 'react-resource-router';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import { urlRefsAndTarget } from '@atlassian/help-center-common-util/is-external-resource';
import articleMessages from '../articles/messages';
import {
    INITIAL_REQUEST_FORMS_COUNT,
    Title,
    LinkContainer,
    Snippet,
    ResultItemContainer,
    ResultTitleContainer,
    formatText,
    getExternalResourcesCount,
} from '../common';
import { sendSearchOperationalAnalyticEvents, sendSearchUIAnalyticEvents } from '../common/utils';
import { ResourceIcon } from '../resource-icon';
import type { requestFormsFragment$key } from './__generated__/requestFormsFragment.graphql';
import messages from './messages';

export interface Props {
    term: string;
    result: requestFormsFragment$key;
    updateResultsCount: (count: number) => void;
    createAnalyticsEvent?: CreateUIAnalyticsEvent;
}

export const RequestForms = ({ result, term, updateResultsCount, createAnalyticsEvent }: Props) => {
    const { formatMessage } = useIntl();
    const data = useFragment<requestFormsFragment$key>(
        graphql`
            fragment requestFormsFragment on HelpObjectStoreQueryApi {
                requestForms: searchHelpObjects(
                    searchInput: {
                        cloudId: $cloudId
                        queryTerm: $queryTerm
                        resultLimit: $resultLimit
                        entityType: REQUEST_FORM
                    }
                ) @required(action: THROW) {
                    ... on HelpObjectStoreSearchResult {
                        __typename @required(action: THROW)
                        id @required(action: THROW)
                        title @required(action: THROW)
                        description @required(action: THROW)
                        displayLink @required(action: THROW)
                        containerDisplayName
                        isExternal @required(action: THROW)
                        ...resourceIconFragment
                    }

                    ... on HelpObjectStoreQueryError {
                        message
                    }
                }
            }
        `,
        result
    );
    const [portalId] = usePathParam('portalId');

    const slicedRequestForms = data.requestForms.slice(0, INITIAL_REQUEST_FORMS_COUNT);
    const showSection = slicedRequestForms.length > 0;
    const externalResourcesCount = useMemo(() => getExternalResourcesCount(slicedRequestForms), [slicedRequestForms]);
    // Since fetching request-forms is a bulk call in backend, we consider an error has occurred if all request-forms have an error message
    const hasError = slicedRequestForms.findIndex((requestForm) => requestForm?.message !== undefined) !== -1;

    useEffect(() => {
        if (showSection && hasError) {
            sendSearchOperationalAnalyticEvents({ action: 'requestTypeSearchFailed' }, createAnalyticsEvent, portalId);
        }

        if (showSection && !hasError) {
            sendSearchUIAnalyticEvents(
                { externalResourcesCount, resourceType: 'request-form', resourceCount: slicedRequestForms.length },
                createAnalyticsEvent,
                portalId
            );

            sendSearchOperationalAnalyticEvents(
                { action: 'requestTypeSearchSucceeded' },
                createAnalyticsEvent,
                portalId
            );
        }
    }, [
        term,
        showSection,
        externalResourcesCount,
        slicedRequestForms.length,
        createAnalyticsEvent,
        hasError,
        portalId,
    ]);

    useEffect(() => {
        updateResultsCount(slicedRequestForms.length);
    }, [term, slicedRequestForms.length, updateResultsCount]);

    const getDescription = (containerDisplayName?: string | null, description?: string) => {
        if (!containerDisplayName) {
            return formatText(description, term);
        }
        return `${formatMessage(messages.portal)}: ${formatText(containerDisplayName, term)}`;
    };

    return (
        <>
            {showSection && (
                <>
                    <ResultTitleContainer>{formatMessage(messages.requestForms)}</ResultTitleContainer>

                    {slicedRequestForms.map((resource) => {
                        if (resource?.__typename === 'HelpObjectStoreSearchResult') {
                            const {
                                id,
                                title,
                                description,
                                displayLink = '',
                                containerDisplayName = '',
                                isExternal = false,
                            } = resource;
                            const shortcutIcon = isExternal ? (
                                <ShortcutIcon label={formatMessage(articleMessages.shortcutIcon)} size="small" />
                            ) : null;
                            const highlightedTitle = title ? formatText(title, term) : '';
                            const highlightedDescription = getDescription(containerDisplayName, description);
                            const linkProps = urlRefsAndTarget({ displayLink, isAnExternalResource: isExternal });

                            return (
                                <LinkContainer
                                    key={id}
                                    actionSubjectId={isExternal ? 'external-request-form-link' : 'request-form-link'}
                                    data-test-id="result-link-container"
                                    {...linkProps}
                                >
                                    <ResourceIcon result={resource} />
                                    <ResultItemContainer>
                                        <Title dangerouslySetInnerHTML={{ __html: sanitize(highlightedTitle) }} />
                                        <Snippet
                                            dangerouslySetInnerHTML={{ __html: sanitize(highlightedDescription) }}
                                        />
                                    </ResultItemContainer>
                                    {shortcutIcon}
                                </LinkContainer>
                            );
                        }
                        return null;
                    })}
                </>
            )}
        </>
    );
};

export default withAnalyticsEvents()(RequestForms);
